/** Normalize possibly varying API field names for admin dashboards. */
export function pickFirst(obj, keys, fallback = undefined) {
  if (!obj || typeof obj !== "object") return fallback;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return fallback;
}

/** Australian states + mainland territories (filter / normalisation). */
/** Alphabetical by jurisdiction name (filter dropdown order). */
export const AUSTRALIAN_STATE_MENU = [
  { code: "ACT", label: "Australian Capital Territory" },
  { code: "NSW", label: "New South Wales" },
  { code: "NT", label: "Northern Territory" },
  { code: "QLD", label: "Queensland" },
  { code: "SA", label: "South Australia" },
  { code: "TAS", label: "Tasmania" },
  { code: "VIC", label: "Victoria" },
  { code: "WA", label: "Western Australia" },
];

const AU_STATE_NAME_TO_CODE = (() => {
  const m = Object.create(null);
  for (const { code, label } of AUSTRALIAN_STATE_MENU) {
    m[code.toLowerCase()] = code;
    m[label.toLowerCase()] = code;
  }
  return m;
})();

/** Returns NSW|VIC|…|NT or "" if missing / not recognised. */
export function normalizeAustralianStateCode(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (AU_STATE_NAME_TO_CODE[lower]) return AU_STATE_NAME_TO_CODE[lower];
  const upper = raw.toUpperCase();
  if (AUSTRALIAN_STATE_MENU.some((s) => s.code === upper)) return upper;
  return "";
}

/**
 * Maps GET adminOverviewV1/AdminOverview response (see API docs).
 */
export function normalizeAdminOverviewResponse(raw) {
  const r = raw || {};
  const summary = r.SummaryCards || r.summaryCards || {};
  const chemists = r.ChemistTable || r.chemistTable || [];
  const list = Array.isArray(chemists) ? chemists : [];
  const groups = Array.isArray(r.Groups) ? r.Groups : [];
  return {
    summaryCards: {
      totalChemistsUsingApps: pickFirst(summary, [
        "TotalChemistsUsingApps",
        "totalChemistsUsingApps",
      ]),
      qudaSubscriptions: pickFirst(summary, ["QudaSubscriptions", "qudaSubscriptions"]),
      mediDropSubscriptions: pickFirst(summary, ["MediDropSubscriptions", "mediDropSubscriptions"]),
      activeHaloPay: pickFirst(summary, ["ActiveHaloPay", "activeHaloPay"]),
      // Per-app { Active, Trialing } counts (Trialing null for boolean-flag apps).
      apps: summary.Apps || summary.apps || null,
    },
    chemistTable: list,
    selectedChemistDetail: r.SelectedChemistDetail ?? null,
    groups,
    dateRange: r.DateRange || null,
  };
}

/**
 * Per-chemist appointment counts by DocLink provider (Medmate / Hubmed / Hola).
 * AdminDocLinkOverview OverviewTable rows may include:
 * `MedmateAppointments`, `HubmedAppointments`, `HolaAppointments`, and/or
 * `ProviderAppointmentCounts`: { Medmate, Hubmed, Hola }.
 */
export function getDocLinkProviderAppointmentCount(row, providerLabel) {
  if (!row || typeof row !== "object") return null;
  const p = providerLabel;
  const tryNum = (v) =>
    v != null && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null;

  const flatKeys = [
    `${p}Appointments`,
    `${p}Count`,
    `Appointments${p}`,
    `Total${p}Appointments`,
  ];
  for (const k of flatKeys) {
    if (row[k] != null && row[k] !== "") {
      const n = tryNum(row[k]);
      if (n != null) return n;
    }
  }

  const bag =
    row.ProviderAppointmentCounts ||
    row.providerAppointmentCounts ||
    row.ProviderCounts ||
    row.providerCounts ||
    row.AppointmentsByProvider;
  if (bag && typeof bag === "object") {
    const keys = [p, p.toLowerCase(), p.toUpperCase()];
    for (const k of keys) {
      if (bag[k] != null && bag[k] !== "") {
        const n = tryNum(bag[k]);
        if (n != null) return n;
      }
    }
  }
  return null;
}

export function mapAdminOverviewTableRow(row, index) {
  const uuid = row?.ChemistUUID ?? "";
  const dedicated = row?.SMSDedicatedNumber ?? row?.smsDedicatedNumber;
  return {
    id: uuid || String(index),
    chemistUUID: uuid,
    chemistName: row?.ChemistName,
    dispenseSystemName: pickFirst(row, [
      "DispenseSystemName",
      "dispenseSystemName",
    ]),
    dispenseType: pickFirst(row, ["DispenseType", "dispenseType"]),
    totalQudaOrders: pickFirst(row, ["TotalQudaOrders", "totalQudaOrders"]),
    qudaActive: pickFirst(row, ["QudaActive", "qudaActive"]),
    mediDropActive: row?.MediDropActive,
    haloPayActive: row?.HaloPayActive,
    docLinkActive: row?.DocLinkActive,
    consults30Days: row?.ConsultsIn30Days,
    qudaOrders30Days: row?.QudaOrdersIn30Days,
    sms30Days: row?.TotalSmsSentIn30Days,
    haloPayTransactions30Days: row?.HaloPayTransactionsIn30Days,
    deliveries30Days: row?.DeliveriesIn30Days,
    totalTerminals: row?.TotalTerminals,
    totalStaff: row?.TotalStaff,
    chemistID: row?.ChemistID,
    chemistState: pickFirst(row, ["State", "state"]),
    broadcastActive: dedicated != null && String(dedicated).trim() !== "",
    smsDedicatedNumber: dedicated ?? null,
    groupID: row?.GroupID ?? null,
    groupName: row?.GroupName ?? null,
  };
}
